import { EMPTY_OBJECT } from "@multiweb/reactUtils";
import { IAttrValue } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { ExtendedPropertiesType } from "UI/types";
import { IAwaitableEvent } from "UI/UI.BaseControls/AwaitableEvent";
import { DataGridSharedMethods } from "UI/UI.Controls/DataGridSharedMethods";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ITreeViewRowData } from "../../DataSource/TreeViewDataSource";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { IDataRow, IDataRowElement } from "./IDataRow";
import { TableCell } from "./TableCell";

export interface TreeViewDataRow {
    addEventListener(type: "SelectionChangedPreview", listener: (ev: IAwaitableEvent<boolean>) => void, useCapture?: boolean): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;
}

export class TreeViewDataRow extends HTMLElement implements IDataRowElement {
    public readonly cellValidityChanged: GlobalEvent<IValidatable, IValidationResult> = new GlobalEvent<IValidatable, IValidationResult>();
    public readonly selectionStateChanged: GlobalEvent<IDataRow, boolean> = new GlobalEvent<IDataRow, boolean>();

    public readonly isContainer: boolean = true;
    public isValid: boolean = true;
    public flag: Enum | string;

    private _isAttached: boolean;
    protected _dataKey: string;
    private _state: CollapseState;
    private _expandDataLoaded = false;
    private _isLastNode = false;

    private _isSelected: boolean;
    public isDeleted: boolean;

    //public parentDataKey: string = null;
    private _isDataLoadedDefferred = PromiseUtils.deferStateful<void>();

    private _nameContainerElement: HTMLElement;
    protected _childrenElement: HTMLElement;
    private _expandElement: HTMLElement;
    protected _dataElement: HTMLElement;
    private _loaderElement: HTMLElement;

    protected _dataGrid: IDataGrid<IDataRowElement>;
    protected _dataCells: Array<TableCell> = [];
    protected _fullDataCells: Array<TableCell> = [];

    //#region Properties
    public get dataGrid(): IDataGrid<IDataRowElement> {
        return this._dataGrid;
    }

    public get dataCells(): readonly TableCell[] {
        return this._dataCells;
    }

    public set isLastNode(v: boolean) {
        this._isLastNode = v;
        this.classList.toggle("last-node", v);
        if (this._loaderElement)
            this._loaderElement.remove();
    }

    public get isLastNode(): boolean {
        return this._isLastNode;
    }

    public set isSelected(v: boolean) {
        if (this._isSelected === v) {
            return;
        }

        this._isSelected = v;
        this.classList.toggle("selected", v);

        if (v) {
            // Vyvoláme událost a informujeme, že došlo ke změně vybraného záznamu.
            this.callEvent("selectedRecordChanged");
        }
    }

    public get isSelected(): boolean {
        return this._isSelected;
    }

    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this.classList.contains("disabled");
    }

    public get isAttached(): boolean {
        return this._isAttached;
    }

    public get state(): CollapseState {
        return this._state;
    }

    public set state(v: CollapseState) {
        this.toggleExpand(v);
    }

    public get isDataLoaded(): boolean {
        return this._isDataLoadedDefferred.isSettled;
    }

    public get childRows(): TreeViewDataRow[] {
        if (!this._childrenElement)
            return [];
        return Array.from(this._childrenElement.children) as TreeViewDataRow[];
    }

    public get parentRow(): TreeViewDataRow {
        if (this.parentElement && this.parentElement.parentElement instanceof TreeViewDataRow) {
            return this.parentElement.parentElement;
        }

        return null;
    }

    public get dataLoadPromise(): Promise<void> {
        return this._isDataLoadedDefferred.promise;
    }

    /**
     * ID radku
     */
    public get dataKey(): string {
        return this._dataKey;
    }

    public set dataKey(v: string) {
        this._dataKey = v;
        this.setAttribute("datakey", v);
    }

    protected static get observedAttributes(): string[] {
        return ["state"];
    }

    //#endregion Properties

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    public addCells(columns: Column[]): void {
        this._initCells(null, columns);
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);

        this.classList.add("tree-view-datarow");

        this.setAttribute("state", "collapsed");

        this._nameContainerElement = createElement({ classes: ["name-container"] });
        this._expandElement = createElement({ type: "div", classes: ["btn", "flat", "expander", "icon", "ui-triangle-right"] });
        this._dataElement = createElement({ classes: ["data"] });
        this._nameContainerElement.appendChild(this._expandElement);
        this._nameContainerElement.appendChild(this._dataElement);

        this._childrenElement = createElement({ type: "ul", classes: ["children"] });
        this._loaderElement = createElement({ classes: ["loader", "loading"] });
        Localization.getTextPromise("GUI-580532").then((v) => this._loaderElement.textContent = v); // Načítám

        this.appendChild(this._nameContainerElement);
        this.appendChild(this._childrenElement);
        this._childrenElement.appendChild(this._loaderElement);

        this._expandElement.addEventListener("click", () => {
            if (this._expandElement.classList.contains("Disabled"))
                return;
            this.toggleExpand();
        });

        this._dataElement.addEventListener("click", async () => this._clickDataElement());

        this._dataElement.addEventListener("dblclick", () => {
            this.toggleExpand();
        });

        this.dataLoadPromise.then(() => {
            if (this._loaderElement)
                this._loaderElement.remove();
        });
    }

    public toggleExpand(force?: CollapseState): void {
        if (force == null) {
            this._state = this._state == CollapseState.collapsed ? CollapseState.expanded : CollapseState.collapsed;
        } else {
            if (force == this._state)
                return;
            this._state = force;
        }

        if (!this._expandDataLoaded && this._state == CollapseState.expanded) {
            if (!this.isDataLoaded && !this.isLastNode) {
                this.callEvent("childDataRequest", { promiseResolvedFnc: this._isDataLoadedDefferred.resolve }, true);
            }

            this._expandDataLoaded = true;
        }

        this.setAttribute("state", CollapseState[this._state]);
    }

    /** Handler kliknutí na řádek */
    protected async _clickDataElement(): Promise<void> {
        if (await DataGridSharedMethods.canChangeSelectedRow(this)) {
            // Nastavíme aktuálnímu řádku selected na true.
            this.isSelected = true;
        }
    }

    /**
     * Ziniciuje nový řádek
     * @param rowData Data k zobrazení v řádku
     * @param ownerGrid DataGrid, ke kterému řádek patří
     */
    public init(rowData: ITreeViewRowData, ownerGrid: IDataGrid<IDataRowElement>): void {
        this._dataGrid = ownerGrid;
        this._dataCells = new Array<TableCell>();

        this.dataKey = rowData.data[this._dataGrid.dataSource.dataKey].valueOf().toString();

        if (this._dataKey == undefined)
            throw Error("DataRow has invalid dataKey");

        this._initCells(rowData);

        if (!rowData.hasChildren)
            this.isLastNode = true;
    }

    private _initCells(rowData?: ITreeViewRowData, columns?: Column[]): void {
        if (columns === null || columns === undefined) columns = this._dataGrid.columns; // columns ??= this._dataGrid.columns
        const dataRowCaseInsensitive = ObjectUtils.getPropertiesCaseInsensitive<IAttrValue>(rowData?.data ?? {});

        let cellIndex = this._dataCells.length;
        columns.forEach(column => {
            const data: Array<IAttrValue> = [];

            if (!column.withoutAttribute) {
                data.push(...column.attrNames.map(attrName => dataRowCaseInsensitive.get(attrName)));
            }

            const cell = new TableCell(this._dataGrid, this, cellIndex, column, data);
            this._dataCells.push(cell);
            cellIndex++;
        });
    }

    public render(): HTMLElement {
        for (const cell of this._dataCells) {
            if (cell.column.visible) {
                cell.toEdit(false);
                const newCell = cell.render();
                if (newCell) //sloupec mohl změnit viditelnost a render vrátil null.
                    this._dataElement.appendChild(newCell);
            }
        }

        return this as HTMLElement;
    }

    public select(): void {
        // Prázdná metoda
    }

    public async selectWithCheckChanges(): Promise<void> {
        // Prázdná metoda
    }

    public setData(_attrName: string, _value: IAttrValue): Promise<void> {
        throw new Error("Not implemented");
    }

    // ReSharper disable once InconsistentNaming
    protected connectedCallback(): void {
        this._isAttached = true;
        GenericControl.callEvent(this, "attached");
    }

    // ReSharper disable once InconsistentNaming
    protected disconnectedCallback(): void {
        this._isAttached = false;
        GenericControl.callEvent(this, "detached");
        this.unregisterEventListener();
    }

    // ReSharper disable once InconsistentNaming
    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
        switch (attrName) {
            case "state":
                this.state = CollapseState[newVal as "expanded" | "collapsed"] || CollapseState.collapsed;
                break;
        }
    }

    public override appendChild<T extends Node>(newChild: T): T {
        if (newChild instanceof TreeViewDataRow) {
            return this._childrenElement.appendChild(newChild);
        }
        return super.appendChild(newChild);
    }

    public override insertAdjacentElement(position: "beforebegin" | "afterbegin" | "beforeend" | "afterend", insertedElement: Element): Element {
        if (insertedElement instanceof TreeViewDataRow && (position == "afterbegin" || position == "beforeend")) {
            return this._childrenElement.insertAdjacentElement(position, insertedElement);
        }

        return super.insertAdjacentElement(position, insertedElement);
    }

    /**
     * Zajišťuje zascrollování na element názvu. Celý řádek může být kvůli podřízeným hodně vysoký a nefugovalo by to správně.
     */
    public override scrollIntoView(arg?: boolean | ScrollIntoViewOptions): void {
        this._nameContainerElement.scrollIntoView(arg);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: Event) => void): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number; width: number; height: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    public getExtendedProperties(attrName: string): ExtendedPropertiesType {
        if (this._dataGrid.getExtendedProperties)
            return this._dataGrid.getExtendedProperties(attrName);

        return EMPTY_OBJECT;
    }

    //#endregion Zpracování metod z globální třídy GenericControl

    public dispose(): void {
        this.cellValidityChanged.removeAll();
        this.selectionStateChanged.removeAll();
        this._dataCells?.forEach((cell) => cell.dispose());
        this._dataCells = [];
        this._fullDataCells?.forEach((cell) => cell.dispose());
        this._fullDataCells = [];
    }
}

export enum CollapseState {
    collapsed,
    expanded,
}

registerCustomElement("tsc-TreeViewDataRow", TreeViewDataRow);
